import React, { useRef, useState, useCallback, useLayoutEffect, useMemo, } from 'react';
import { View, Keyboard } from 'react-native';
import emoji from 'node-emoji';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import Alert from 'utils/packages/Alert';
import TextInput from 'utils/packages/TextInput';
import Modal from 'utils/packages/Modal';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { renameTransaction } from 'actions';
import { isWeb } from '../../../constants';
import { useTransaction } from '../hooks/useTransaction';
import useCheckTransactionsModal from '../hooks/useCheckTransactionsModal';
const getTxnName = (transaction, placeholder) => {
    if (transaction) {
        return 'customName' in transaction && transaction.customName
            ? transaction.customName
            : transaction.counterpartName;
    }
    return placeholder || '';
};
const RenameTransactionScreen = ({ navigation, route, transaction, }) => {
    // one of transaction, ids, and groupTxn is expected to be defined and effective.
    const { groupTxn, onSave, ids, keyBack } = route.params;
    const colors = useColors();
    const transactionsArray = useAppSelector((state) => state.transactions.transactionsArray);
    const placeholder = useMemo(() => {
        let previousName;
        if (!ids)
            return previousName;
        const map = new Map(transactionsArray.map((t) => [t.id, t]));
        for (let i = 0; i < ids.length; i++) {
            const txn = map.get(ids[i]);
            const name = txn?.customName || txn?.counterpartName;
            if (!name) {
                return undefined;
            }
            if (i === 0) {
                previousName = name;
            }
            else if (previousName === name) {
                previousName = name;
            }
            else {
                return undefined;
            }
        }
        return previousName;
    }, [ids, transactionsArray]);
    const dispatch = useAppDispatch();
    const initialName = getTxnName(transaction || groupTxn, placeholder);
    const [name, setName] = useState(initialName);
    const [isFetching, setIsFetching] = useState(false);
    const onSavedCallback = useCallback(() => {
        onSave?.(name, setIsFetching);
    }, [name, onSave]);
    const renameSingleTransaction = useRequiredBenefitCallbackWithSpaces(useCallback(async (transaction) => {
        Keyboard.dismiss();
        setIsFetching(true);
        await dispatch(renameTransaction([String(transaction.id)], name));
        setIsFetching(false);
        navigation.goBack();
        onSavedCallback();
    }, [name, onSavedCallback]), 'renameTransactions');
    const choseJustOne = useCallback((transaction) => {
        Modal.hide(() => renameSingleTransaction(transaction));
    }, [renameSingleTransaction]);
    const choseSimilarOnes = useCallback((transaction) => {
        Modal.hide(() => {
            navigation.navigate('EditMultipleTransactionsRename', {
                isSelectedAll: true,
                keyBack: route.key,
                transaction: { ...transaction, customName: name },
                customName: name,
            });
        });
        onSavedCallback();
    }, [name, onSavedCallback, route.key]);
    const checkTransactionsModal = useCheckTransactionsModal(choseJustOne, choseSimilarOnes);
    const onPressSubmit = useCallback(async () => {
        if (name === initialName)
            return;
        if (!name || !name.length) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a merchant.');
            return;
        }
        if (transaction) {
            // show modal when coming from single transaction
            checkTransactionsModal(transaction);
        }
        else if (ids) {
            // submit when coming from multiple transactions
            Keyboard.dismiss();
            setIsFetching(true);
            await dispatch(renameTransaction(ids.map(String), name));
            setIsFetching(false);
            navigation.pop();
            if (ids.length > 1 && keyBack) {
                navigation.navigateDeprecated('LearningScreen', {
                    keyBack,
                    editData: { editType: 'rename', customName: name },
                    transactionsCount: ids.length,
                });
            }
            onSavedCallback();
        }
        else {
            onSavedCallback();
        }
    }, [
        name,
        initialName,
        transaction,
        ids,
        checkTransactionsModal,
        keyBack,
        onSavedCallback,
    ]);
    const showOriginalName = (transaction?.customName != null &&
        name !== transaction?.counterpartName) ||
        (groupTxn?.counterpartName != null && name !== groupTxn?.counterpartName) ||
        (!!initialName && initialName !== name);
    const onPressRevertToOriginal = useCallback(() => {
        setName(transaction?.counterpartName || groupTxn?.counterpartName || initialName);
    }, [groupTxn?.counterpartName, initialName, transaction?.counterpartName]);
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    const styles = useStyles(styleSet);
    return (<AppView isInTab={isWeb} avoidHeader>
      <StatusBar barStyle="dark-content" animated/>
      <KeyboardAvoidingContainerNew flexContainer withFrame={false} withButtonSpace>
        {isWeb ? <Spacer h={72}/> : <Spacer flex/>}
        <View style={styles.inputContainer}>
          <TextInput value={name} ref={textInputRef} testID="nameInput" autoCorrect={false} returnKeyType="done" onChangeText={setName} allowFontScaling={false} style={styles.textInput} onSubmitEditing={onPressSubmit} focusStyle={styles.textInputFocus} underlineColorAndroid="transparent" selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder}/>
          <Text Secondary TextThin-14 style={[styles.originalName, !showOriginalName && styles.hidden]}>
            {transaction?.counterpartName ||
            groupTxn?.counterpartName ||
            initialName}
          </Text>
        </View>
        <Spacer flex/>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingContainerNew sticky>
        <Button custom onPress={onPressRevertToOriginal} title="Revert to original" disabled={!showOriginalName} textColor={colors.text.brand} style={!showOriginalName && styles.hidden}/>
        <Button brand testID="saveButton" title="Save this name" onPress={onPressSubmit} isFetching={isFetching} disabled={name === initialName}/>
      </KeyboardAvoidingContainerNew>
    </AppView>);
};
const RenameTransactionScreenWrapper = (props) => {
    const { navigation } = props;
    const { id, ids, groupTxn, onSave } = props.route.params;
    const { transaction, LoadingOr404 } = useTransaction(id);
    const colors = useColors();
    const showLoadingOr404 = !transaction && !groupTxn && !onSave && !ids;
    useLayoutEffect(() => {
        const title = ids && ids.length > 1 ? 'Rename transactions' : 'Rename transaction';
        navigation.setOptions({
            title,
            headerTitle: showLoadingOr404 ? '' : title,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, ids, showLoadingOr404, transaction]);
    if (showLoadingOr404) {
        return LoadingOr404;
    }
    return <RenameTransactionScreen {...props} transaction={transaction}/>;
};
const styleSet = createStyleSheets((colors) => ({
    inputContainer: {
        justifyContent: 'center',
    },
    textInput: {
        ...(isWeb
            ? {
                paddingVertical: rem(16),
                color: colors.text.secondary,
            }
            : {
                fontSize: rem(25),
                textAlign: 'center',
                color: colors.text.primary,
                fontFamily: 'Montserrat-Light',
            }),
    },
    textInputFocus: {
        color: colors.text.primary,
    },
    originalName: {
        textAlign: 'center',
        marginTop: rem(8),
    },
    hidden: {
        opacity: 0,
    },
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
}));
export default RenameTransactionScreenWrapper;
